import React, { createContext, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';

// Vite exposes runtime env vars through import.meta.env
// use VITE_API_URL to point to your backend (set in Vercel/Render environments)
const RENDER_API_BASE = 'https://teakonn-backend.onrender.com';
const OLD_RAILWAY_HOST = 'teakonn-production.up.railway.app';

function replaceOldBackend(url: string): string {
  return url.includes(OLD_RAILWAY_HOST) ? RENDER_API_BASE : url;
}

function normalizeBase(url?: string): string {
  if (!url) return '/';
  let u = url.trim().replace(/\/$/, '');
  u = replaceOldBackend(u);
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('//')) {
    try {
      return (typeof window !== 'undefined' ? window.location.protocol : 'https:') + u;
    } catch {
      return 'https:' + u;
    }
  }
  if (u.startsWith('/')) {
    // Handle common misconfiguration: VITE_API_URL set as "/host.tld" instead of protocol-relative "//host.tld"
    const afterSlash = u.slice(1);
    const firstSegment = afterSlash.split('/')[0] || '';
    if (!u.startsWith('//') && firstSegment.includes('.')) {
      try {
        const proto = typeof window !== 'undefined' ? window.location.protocol : 'https:';
        return `${proto}//${afterSlash}`;
      } catch {
        return 'https://' + afterSlash;
      }
    }
    try {
      return (typeof window !== 'undefined' ? window.location.origin : '') + u;
    } catch {
      return u;
    }
  }
  const isProd = (import.meta as any)?.env?.MODE === 'production';
  return `${isProd ? 'https://' : 'http://'}${u}`;
}

// Prefer runtime override to keep parity with axios helper
const runtimeBase =
  (typeof window !== 'undefined' && (window as any).__API_URL) ||
  (typeof window !== 'undefined' && localStorage.getItem('API_URL')) ||
  import.meta.env.VITE_API_URL;
try {
  if (typeof window !== 'undefined' && String(runtimeBase || '').includes(OLD_RAILWAY_HOST)) {
    localStorage.removeItem('API_URL');
  }
} catch {}
const API = normalizeBase(runtimeBase) || '/';

// lightweight helper — prefer using localStorage token for handshake
function getTokenFromStorage() {
  try {
    return localStorage.getItem('token');
  } catch (e) {
    return null;
  }
}

export const socket = io(API, {
  autoConnect: false,
  // If you want to use auth token with socket handshake:
  auth: { token: getTokenFromStorage() },
  // Force websocket-only transport for lower latency
  transports: ['websocket'],
  // Avoid attempting HTTP long-polling upgrades
  upgrade: false,
});

// React context
export const SocketContext = createContext<Socket | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Do not auto-connect here. Connection should be triggered by the app
  // once a user + token are available (see App.tsx). On unmount, if
  // the socket is connected we'll disconnect to clean up resources.
  useEffect(() => {
    return () => {
      try {
        if (socket && (socket as any).connected) socket.disconnect();
      } catch (e) {}
    };
  }, []);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};
